function normalizeId(value) {
	const nr = Number(value)
	return Number.isFinite(nr) ? nr : value
}

export function buildSalesChannelsModel(clients, salesChannels) {
	if (!Array.isArray(clients) || !clients.length) {
		return []
	}
	const byClient = new Map()
	for (const entry of salesChannels ?? []) {
		if (!entry?.clientId) {
			continue
		}
		byClient.set(entry.clientId, {
			salesChannelIds: entry.salesChannelIds ?? [],
			resellerCategoryIds: entry.resellerCategoryIds ?? []
		})
	}
	const model = []
	for (const client of clients) {
		const clientAssignment = byClient.get(client.sys.id)
		if (!clientAssignment) {
			continue
		}
		const scIds = new Set(clientAssignment.salesChannelIds ?? [])
		const rcIds = new Set((clientAssignment.resellerCategoryIds ?? []).map(normalizeId))
		const selected = client.fields.salesChannels.de
			.filter(sc => sc.fields?.mandatory?.de || scIds.has(sc.sys.id))
			.map(sc => {
				let selectedCategories = []
				if (sc.fields?.isResellerChannel?.de) {
					const categories = sc.fields.resellerCategories?.de ?? []
					for (const category of categories) {
						const categoryId = normalizeId(category.fields.peakId.de)
						if (rcIds.has(categoryId)) {
							selectedCategories.push(categoryId)
						}
						for (const subCategory of category.fields.subCategories.de ?? []) {
							const subId = normalizeId(subCategory.peakId)
							if (rcIds.has(subId)) {
								selectedCategories.push(subId)
							}
						}
					}
				}
				return {
					id: sc.sys.id,
					title: sc.fields.title,
					isSelected: true,
					selected: selectedCategories
				}
			})
		model.push({
			id: client.sys.id,
			title: client.fields.title,
			available: client.fields.salesChannels.de.length,
			isSelected: true,
			selected
		})
	}
	return model
}

export function buildSalesChannelsArrayFromModel(model) {
	const salesChannels = []
	for (const client of model?.filter(x => x.id !== 'dummy') ?? []) {
		const selectedChannels = client.selected?.filter(x => x.id !== 'dummy') ?? []
		const salesChannelIds = selectedChannels.map(sc => sc.id)
		const resellerCategoryIds = selectedChannels.flatMap(sc => (sc.selected ?? [])
			.map(v => (v && typeof v === 'object') ? v.id : v)
			.filter(v => v !== 'dummy' && v !== undefined && v !== null)
		)
		salesChannels.push({
			clientId: client.id,
			salesChannelIds,
			resellerCategoryIds
		})
	}
	return salesChannels
}

function getCacheMap(cache, type) {
	if (!cache[type]) {
		// #region agent log
		console.log('[ticket-channeling][cache] creating cache map', { type })
		// #endregion
		cache[type] = new Map()
	}
	return cache[type]
}

function getCachedEntity(cache, type, id, buildFn) {
	const map = getCacheMap(cache, type)
	const key = `${type}::${id}`
	if (map.has(key)) {
		// #region agent log
		console.log('[ticket-channeling][cache] hit', { type, key, size: map.size })
		// #endregion
		return map.get(key)
	}
	const value = buildFn()
	map.set(key, value)
	// #region agent log
	console.log('[ticket-channeling][cache] miss->store', { type, key, size: map.size, value })
	// #endregion
	return value
}

function cloneCategoryList(values) {
	const ids = []
	const seen = new Set()
	for (const value of values ?? []) {
		const id = (value && typeof value === 'object') ? value.id : value
		if (id === 'dummy' || id === null || id === undefined) {
			continue
		}
		const normalized = normalizeId(id)
		if (seen.has(normalized)) {
			continue
		}
		seen.add(normalized)
		ids.push(normalized)
	}
	return ids
}

export function mergeSalesChannelsModels(baseModel, additions, lookupCache = {}) {
	const merged = JSON.parse(JSON.stringify(Array.isArray(baseModel) ? baseModel : []))
	// #region agent log
	console.groupCollapsed('[ticket-channeling][merge] start')
	console.log('baseModelCount', Array.isArray(baseModel) ? baseModel.length : 0)
	console.log('additionsCount', Array.isArray(additions) ? additions.length : 0)
	console.log('cacheClientSize', lookupCache?.client?.size ?? 0)
	console.log('cacheChannelSize', lookupCache?.channel?.size ?? 0)
	console.groupEnd()
	// #endregion
	for (const client of additions ?? []) {
		if (!client?.id || client.id === 'dummy') {
			// #region agent log
			console.log('[ticket-channeling][merge] skip invalid client entry', { client })
			// #endregion
			continue
		}
		let targetClient = merged.find(x => x.id === client.id)
		if (!targetClient) {
			targetClient = getCachedEntity(lookupCache, 'client', client.id, () => ({
				id: client.id,
				title: client.title,
				available: client.available ?? 0,
				isSelected: true,
				selected: []
			}))
			merged.push(JSON.parse(JSON.stringify(targetClient)))
			targetClient = merged.find(x => x.id === client.id)
		}

		if (!Array.isArray(targetClient.selected)) {
			targetClient.selected = []
		}

		for (const channel of client.selected ?? []) {
			if (!channel?.id || channel.id === 'dummy') {
				// #region agent log
				console.log('[ticket-channeling][merge] skip invalid channel entry', { clientId: client.id, channel })
				// #endregion
				continue
			}
			let targetChannel = targetClient.selected.find(x => x.id === channel.id)
			if (!targetChannel) {
				targetChannel = getCachedEntity(lookupCache, 'channel', `${client.id}::${channel.id}`, () => ({
					id: channel.id,
					title: channel.title,
					isSelected: true,
					selected: []
				}))
				targetClient.selected.push(JSON.parse(JSON.stringify(targetChannel)))
				targetChannel = targetClient.selected.find(x => x.id === channel.id)
			}

			const existing = cloneCategoryList(targetChannel.selected)
			const incoming = cloneCategoryList(channel.selected)
			const mergedCategorySet = new Set(existing)
			incoming.forEach(id => mergedCategorySet.add(id))
			targetChannel.selected = Array.from(mergedCategorySet)
			// #region agent log
			console.log('[ticket-channeling][merge] channel merged', {
				clientId: client.id,
				channelId: channel.id,
				existingCount: existing.length,
				incomingCount: incoming.length,
				mergedCount: targetChannel.selected.length,
				mergedCategories: targetChannel.selected
			})
			// #endregion
		}
	}
	// #region agent log
	console.groupCollapsed('[ticket-channeling][merge] done')
	console.log('mergedClientCount', merged.length)
	console.log('cacheClientSize', lookupCache?.client?.size ?? 0)
	console.log('cacheChannelSize', lookupCache?.channel?.size ?? 0)
	console.log('mergedModel', merged)
	console.groupEnd()
	// #endregion
	return merged
}
